import React, { useState } from 'react';

interface ConfigFormProps {
  config: Record<string, unknown>;
  onChange: (config: Record<string, unknown>) => void;
}

const inputClass = 'w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';
const labelClass = 'block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5';
const selectClass = inputClass;

// ── 1. create_task ──
const CreateTaskForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Title</label>
      <input type="text" className={inputClass} value={(config.title as string) ?? ''} onChange={(e) => onChange({ ...config, title: e.target.value })} placeholder="Task title" />
    </div>
    <div>
      <label className={labelClass}>Description</label>
      <textarea className={inputClass} rows={2} value={(config.description as string) ?? ''} onChange={(e) => onChange({ ...config, description: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Assignee Role</label>
      <input type="text" className={inputClass} value={(config.assignee_role as string) ?? ''} onChange={(e) => onChange({ ...config, assignee_role: e.target.value })} placeholder="e.g. attorney" />
    </div>
    <div>
      <label className={labelClass}>Priority</label>
      <select className={selectClass} value={(config.priority as string) ?? 'normal'} onChange={(e) => onChange({ ...config, priority: e.target.value })}>
        <option value="low">Low</option>
        <option value="normal">Normal</option>
        <option value="high">High</option>
        <option value="urgent">Urgent</option>
      </select>
    </div>
    <div>
      <label className={labelClass}>Due Date Expression</label>
      <input type="text" className={inputClass} value={(config.due_date_expression as string) ?? ''} onChange={(e) => onChange({ ...config, due_date_expression: e.target.value })} placeholder="e.g. +5 business days" />
    </div>
  </div>
);

// ── 2. create_subtask ──
const CreateSubtaskForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Title</label>
      <input type="text" className={inputClass} value={(config.title as string) ?? ''} onChange={(e) => onChange({ ...config, title: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Assignee Role</label>
      <input type="text" className={inputClass} value={(config.assignee_role as string) ?? ''} onChange={(e) => onChange({ ...config, assignee_role: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Due Date Expression</label>
      <input type="text" className={inputClass} value={(config.due_date_expression as string) ?? ''} onChange={(e) => onChange({ ...config, due_date_expression: e.target.value })} placeholder="e.g. +3 calendar days" />
    </div>
  </div>
);

// ── 3. calculate_deadline ──
const CalculateDeadlineForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Statute Reference</label>
      <input type="text" className={inputClass} value={(config.statute_reference as string) ?? ''} onChange={(e) => onChange({ ...config, statute_reference: e.target.value })} placeholder="e.g. CCP §583.310" />
    </div>
    <div>
      <label className={labelClass}>Base Date Expression</label>
      <input type="text" className={inputClass} value={(config.base_date_expression as string) ?? ''} onChange={(e) => onChange({ ...config, base_date_expression: e.target.value })} placeholder="e.g. task.filed_date" />
    </div>
    <div>
      <label className={labelClass}>Direction</label>
      <select className={selectClass} value={(config.direction as string) ?? 'after'} onChange={(e) => onChange({ ...config, direction: e.target.value })}>
        <option value="before">Before</option>
        <option value="after">After</option>
      </select>
    </div>
    <div>
      <label className={labelClass}>Day Type</label>
      <select className={selectClass} value={(config.day_type as string) ?? 'calendar'} onChange={(e) => onChange({ ...config, day_type: e.target.value })}>
        <option value="calendar">Calendar</option>
        <option value="business">Business</option>
        <option value="court">Court</option>
      </select>
    </div>
  </div>
);

// ── 4. create_calendar_event ──
const CreateCalendarEventForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Title</label>
      <input type="text" className={inputClass} value={(config.title as string) ?? ''} onChange={(e) => onChange({ ...config, title: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Date Expression</label>
      <input type="text" className={inputClass} value={(config.date_expression as string) ?? ''} onChange={(e) => onChange({ ...config, date_expression: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Duration (minutes)</label>
      <input type="number" className={inputClass} value={(config.duration_minutes as number) ?? 60} onChange={(e) => onChange({ ...config, duration_minutes: Number(e.target.value) })} />
    </div>
    <div>
      <label className={labelClass}>Attendee Roles (comma-separated)</label>
      <input type="text" className={inputClass} value={(config.attendee_roles as string) ?? ''} onChange={(e) => onChange({ ...config, attendee_roles: e.target.value })} placeholder="attorney, client" />
    </div>
  </div>
);

// ── 5. create_reminder ──
const CreateReminderForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Title</label>
      <input type="text" className={inputClass} value={(config.title as string) ?? ''} onChange={(e) => onChange({ ...config, title: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Trigger Date Expression</label>
      <input type="text" className={inputClass} value={(config.trigger_date_expression as string) ?? ''} onChange={(e) => onChange({ ...config, trigger_date_expression: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Recipient Role</label>
      <input type="text" className={inputClass} value={(config.recipient_role as string) ?? ''} onChange={(e) => onChange({ ...config, recipient_role: e.target.value })} />
    </div>
  </div>
);

// ── 6. send_notification ──
const SendNotificationForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Recipient Role</label>
      <input type="text" className={inputClass} value={(config.recipient_role as string) ?? ''} onChange={(e) => onChange({ ...config, recipient_role: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Notification Type</label>
      <select className={selectClass} value={(config.type as string) ?? 'in_app'} onChange={(e) => onChange({ ...config, type: e.target.value })}>
        <option value="in_app">In-App</option>
        <option value="email">Email</option>
        <option value="both">Both</option>
      </select>
    </div>
    <div>
      <label className={labelClass}>Template</label>
      <input type="text" className={inputClass} value={(config.template as string) ?? ''} onChange={(e) => onChange({ ...config, template: e.target.value })} placeholder="e.g. transition_complete" />
    </div>
  </div>
);

// ── 7. attach_form ──
const AttachFormForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Form Number</label>
      <input type="text" className={inputClass} value={(config.form_number as string) ?? ''} onChange={(e) => onChange({ ...config, form_number: e.target.value })} placeholder="e.g. FL-100" />
    </div>
    <div>
      <label className={labelClass}>Form Title</label>
      <input type="text" className={inputClass} value={(config.form_title as string) ?? ''} onChange={(e) => onChange({ ...config, form_title: e.target.value })} />
    </div>
  </div>
);

// ── 8. resolve_court_rules ──
const ResolveCourtRulesForm: React.FC<ConfigFormProps> = () => (
  <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">
    No configuration needed — reads court rules from runtime_data at execution time.
  </p>
);

// ── 9. generate_court_response_tasks ──
const GenerateCourtResponseTasksForm: React.FC<ConfigFormProps> = () => (
  <p className="text-[10px] text-gray-400 dark:text-gray-500 italic">
    No configuration needed — generates response tasks based on court filing context.
  </p>
);

// ── 10. instantiate_template ──
const InstantiateTemplateForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Template ID or Slug</label>
      <input type="text" className={inputClass} value={(config.template_id as string) ?? ''} onChange={(e) => onChange({ ...config, template_id: e.target.value })} placeholder="Template identifier" />
    </div>
  </div>
);

// ── 11. trigger_playbook ──
const TriggerPlaybookForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Playbook Slug</label>
      <input type="text" className={inputClass} value={(config.playbook_slug as string) ?? ''} onChange={(e) => onChange({ ...config, playbook_slug: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Task Type Slug</label>
      <input type="text" className={inputClass} value={(config.task_type_slug as string) ?? ''} onChange={(e) => onChange({ ...config, task_type_slug: e.target.value })} />
    </div>
  </div>
);

// ── 12. transition_sibling_playbook ──
const TransitionSiblingPlaybookForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Playbook Slug</label>
      <input type="text" className={inputClass} value={(config.playbook_slug as string) ?? ''} onChange={(e) => onChange({ ...config, playbook_slug: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Transition Slug</label>
      <input type="text" className={inputClass} value={(config.transition_slug as string) ?? ''} onChange={(e) => onChange({ ...config, transition_slug: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>On Missing</label>
      <select className={selectClass} value={(config.on_missing as string) ?? 'skip'} onChange={(e) => onChange({ ...config, on_missing: e.target.value })}>
        <option value="skip">Skip</option>
        <option value="error">Error</option>
      </select>
    </div>
    <div>
      <label className={labelClass}>On Failure</label>
      <select className={selectClass} value={(config.on_failure as string) ?? 'log'} onChange={(e) => onChange({ ...config, on_failure: e.target.value })}>
        <option value="log">Log</option>
        <option value="block">Block</option>
        <option value="rollback">Rollback</option>
      </select>
    </div>
  </div>
);

// ── 13. update_field ──
const UpdateFieldForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Target</label>
      <select className={selectClass} value={(config.target as string) ?? 'task'} onChange={(e) => onChange({ ...config, target: e.target.value })}>
        <option value="task">Task</option>
        <option value="matter">Matter</option>
      </select>
    </div>
    <div>
      <label className={labelClass}>Field</label>
      <input type="text" className={inputClass} value={(config.field as string) ?? ''} onChange={(e) => onChange({ ...config, field: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Value Expression</label>
      <input type="text" className={inputClass} value={(config.value_expression as string) ?? ''} onChange={(e) => onChange({ ...config, value_expression: e.target.value })} placeholder="e.g. 'completed' or context.now" />
    </div>
  </div>
);

// ── 14. set_runtime_data ──
const SetRuntimeDataForm: React.FC<ConfigFormProps> = ({ config, onChange }) => (
  <div className="space-y-2">
    <div>
      <label className={labelClass}>Key</label>
      <input type="text" className={inputClass} value={(config.key as string) ?? ''} onChange={(e) => onChange({ ...config, key: e.target.value })} />
    </div>
    <div>
      <label className={labelClass}>Value Expression</label>
      <input type="text" className={inputClass} value={(config.value_expression as string) ?? ''} onChange={(e) => onChange({ ...config, value_expression: e.target.value })} />
    </div>
  </div>
);

// ── 15. webhook ──
const WebhookActionForm: React.FC<ConfigFormProps> = ({ config, onChange }) => {
  const [headerKey, setHeaderKey] = useState('');
  const [headerVal, setHeaderVal] = useState('');
  const headers = (config.headers ?? {}) as Record<string, string>;

  const addHeader = () => {
    if (!headerKey.trim()) return;
    onChange({ ...config, headers: { ...headers, [headerKey]: headerVal } });
    setHeaderKey('');
    setHeaderVal('');
  };

  const removeHeader = (key: string) => {
    const next = { ...headers };
    delete next[key];
    onChange({ ...config, headers: next });
  };

  return (
    <div className="space-y-2">
      <div>
        <label className={labelClass}>URL</label>
        <input type="text" className={inputClass} value={(config.url as string) ?? ''} onChange={(e) => onChange({ ...config, url: e.target.value })} placeholder="https://..." />
      </div>
      <div>
        <label className={labelClass}>Method</label>
        <select className={selectClass} value={(config.method as string) ?? 'POST'} onChange={(e) => onChange({ ...config, method: e.target.value })}>
          <option value="POST">POST</option>
          <option value="PUT">PUT</option>
          <option value="PATCH">PATCH</option>
          <option value="GET">GET</option>
        </select>
      </div>
      <div>
        <label className={labelClass}>Headers</label>
        {Object.entries(headers).map(([k, v]) => (
          <div key={k} className="flex items-center gap-1 mb-1">
            <span className="text-[9px] font-mono text-gray-600 dark:text-gray-400">{k}: {v}</span>
            <button type="button" onClick={() => removeHeader(k)} className="text-[9px] text-rose-500 hover:text-rose-700">×</button>
          </div>
        ))}
        <div className="flex gap-1">
          <input type="text" className={`${inputClass} flex-1`} value={headerKey} onChange={(e) => setHeaderKey(e.target.value)} placeholder="Key" />
          <input type="text" className={`${inputClass} flex-1`} value={headerVal} onChange={(e) => setHeaderVal(e.target.value)} placeholder="Value" />
          <button type="button" onClick={addHeader} className="rounded border border-gray-300 px-2 text-xs text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700">+</button>
        </div>
      </div>
      <div>
        <label className={labelClass}>Body (JSON)</label>
        <textarea className={`${inputClass} font-mono`} rows={4} value={(config.body as string) ?? ''} onChange={(e) => onChange({ ...config, body: e.target.value })} placeholder='{"key": "value"}' />
      </div>
      <div>
        <label className={labelClass}>Signing Secret</label>
        <input type="password" className={inputClass} value={(config.signing_secret as string) ?? ''} onChange={(e) => onChange({ ...config, signing_secret: e.target.value })} />
      </div>
      <div>
        <label className={labelClass}>Timeout (ms)</label>
        <input type="number" className={inputClass} value={(config.timeout_ms as number) ?? 30000} onChange={(e) => onChange({ ...config, timeout_ms: Number(e.target.value) })} />
      </div>
    </div>
  );
};

// ── Action Form Router ──
const ACTION_FORMS: Record<string, React.FC<ConfigFormProps>> = {
  create_task: CreateTaskForm,
  create_subtask: CreateSubtaskForm,
  calculate_deadline: CalculateDeadlineForm,
  create_calendar_event: CreateCalendarEventForm,
  create_reminder: CreateReminderForm,
  send_notification: SendNotificationForm,
  attach_form: AttachFormForm,
  resolve_court_rules: ResolveCourtRulesForm,
  generate_court_response_tasks: GenerateCourtResponseTasksForm,
  instantiate_template: InstantiateTemplateForm,
  trigger_playbook: TriggerPlaybookForm,
  transition_sibling_playbook: TransitionSiblingPlaybookForm,
  update_field: UpdateFieldForm,
  set_runtime_data: SetRuntimeDataForm,
  webhook: WebhookActionForm,
};

interface ActionConfigFormProps extends ConfigFormProps {
  actionType: string;
}

export const ActionConfigForm: React.FC<ActionConfigFormProps> = ({ actionType, ...rest }) => {
  const Form = ACTION_FORMS[actionType];
  if (!Form) return <p className="text-[10px] text-gray-400 italic">Unknown action type: {actionType}</p>;
  return <Form {...rest} />;
};
